import React, { useEffect, useRef, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { BaseLink } from '../../../base/Link/BaseLink';
import { BoxComponent } from '../../../base/box/Box';
import { styles } from '../styles';
import { HeaderProps } from '../types';
import { truncateLabel } from '../shared/helpers';
import {
  BUILDER_EDITOR_ELEMENT_ID,
  HEADER_STATE,
  generateUrl,
  handleNavigationPreviewMode,
  headerInitialProps,
} from '../../Commons';
import CtaButton from '../../../../src/components/CtaButton';
import { getDesktopStyles, getBuilderDesktopStyles } from '../shared/helpers';
import { Image } from '../../SharedComponents';
import { Box } from '@mui/material';
import { ILink } from '@zeus/shared';
import { useHeaderVariantTwoStyles } from '../styles/useHeaderVariantTwoStyles';
import styled from '@emotion/styled';
import { UniversalLink } from '../../../components/UniversalLink';

const MobileVariant = React.lazy(() => import('../shared/Components/MobileVariant'));

const StyledLink = styled(UniversalLink, {
  shouldForwardProp: (prop) => prop !== 'customStyles',
})`
  ${(props: any) => props.customStyles}
`;

const HeaderVariantTwo = ({
  globalStyles = headerInitialProps.globalStyles,
  localStyles = headerInitialProps.localStyles,
  componentConfig = headerInitialProps.componentConfig,
  builderConfig = headerInitialProps.builderConfig,
  MediaMap,
  LinkComponent,
  ImageComponent,
  onTextSelect,
}: HeaderProps) => {
  const blockLinks = 'javascript:void(0)';
  const {
    button: rightButton,
    navLinks: { toggle: showNavLinks },
    background,
    allCapsToggle,
  } = componentConfig;
  const {
    logoConfig: {
      header: { primary: logoProperties, secondary: logoSecondaryProperties },
    },
    navLinks,
    pageUrls,
    isBuilderDesktopMode,
    isPublishedDesktopMode,
    builderMode,
    previewMode,
  } = builderConfig;

  const headerState = globalStyles.headerState;
  const initialLeftNavLinks = navLinks.slice(0, 4);
  const initialRightNavLinks = navLinks.slice(4);

  const isDesktop = isBuilderDesktopMode || isPublishedDesktopMode;

  const [isActive, setIsActive] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Determine which logo to use based on desktop/mobile and scroll state
  const getLogoImage = () => {
    // Check for mobile-specific logo
    if (!isDesktop && logoProperties?.['media-s']?.image) {
      return logoProperties['media-s'].image;
    }
    // Check for scroll state logo
    if (headerState === HEADER_STATE.SCROLL_UP && scrolled) {
      return logoSecondaryProperties?.image || logoProperties.image || '';
    }
    return logoProperties.image || '';
  };
  const [visibleLeftNavLinks, setVisibleLeftNavLinks] = useState([...initialLeftNavLinks]);
  const [visibleRightNavLinks, setVisibleRightNavLinks] = useState([...initialRightNavLinks]);
  const [leftDropdownNavLinks, setLeftDropdownNavLinks] = useState<ILink[]>([]);
  const [rightDropdownNavLinks, setRightDropdownNavLinks] = useState<ILink[]>([]);
  const [isWholeNavbarParsed, setIsWholeNavbarParsed] = useState(false);
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);
  const [hoveredLinkIndex, setHoveredLinkIndex] = useState<string | null>(null);
  const [hoveredMoreButton, setHoveredMoreButton] = useState(false);

  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Refs for measuring elements
  const headerRef = useRef(null);
  const logoContainerRef = useRef(null);
  const leftNavLinksContainerRef = useRef(null);
  const rightNavLinksContainerRef = useRef(null);
  const leftNavLinkRefs = useRef([]);
  const rightNavLinkRefs = useRef([]);
  const buttonContainerRef = useRef(null);

  // Styles hook
  const {
    IconStyles,
    buttonStyles,
    navLinkStyles,
    navLinkStyles_Mobile,
    dropdownContainerStyles,
    dropdownButtonStyles,
    dropdownMenuStyles,
    dropdownItemStyles,
    dropdownMobileItemStyles,
    navLinkContainerStyles,
    getHeaderStyles,
    navlinkWrapperStyles,
    updatedStyles,
  } = useHeaderVariantTwoStyles({
    previewMode,
    globalStyles,
    localStyles,
    background,
    rightButton,
    headerState,
    scrolled,
    builderMode,
    isDesktop,
    isActive,
    isWholeNavbarParsed,
    designPreview: builderConfig?.builderMode && builderConfig?.previewMode,
  });

  const lineHeight = '30px';

  const toggleActiveClass = () => {
    setIsActive(!isActive);
  };

  const handleButtonClick = (button: any) => {
    if (previewMode) {
      handleNavigationPreviewMode(button);
    }
  };

  const handleNavLinkClick = (navLink: any) => {
    handleButtonClick(navLink);
    setIsActive(false);
    setHoveredLinkIndex(null);
  };

  const handleLinkHover = (index: string | null) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setHoveredLinkIndex(index);
  };

  const handleMoreButtonHover = (isHovered: boolean) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setHoveredMoreButton(isHovered);
  };

  const isTextMultiline = (element: HTMLElement): boolean => {
    if (!element) return false;
    const numericLineHeight = parseFloat(lineHeight);
    const numLines = Math.round(element.clientHeight / numericLineHeight);
    return numLines > 1;
  };

  const checkNavLinksWrapping = () => {
    if (!isDesktop || navLinks.length === 0) return;

    // Check left side nav links
    let leftVisibleCount = visibleLeftNavLinks.length;
    for (let i = 0; i < visibleLeftNavLinks.length; i++) {
      const el = leftNavLinkRefs.current[i];

      if (!el) continue;

      if (isTextMultiline(el)) {
        leftVisibleCount = Math.max(0, leftVisibleCount - i - 1);
        setVisibleLeftNavLinks(initialLeftNavLinks.slice(0, leftVisibleCount));
        setLeftDropdownNavLinks(initialLeftNavLinks.slice(leftVisibleCount));
        return;
      }
    }

    // Check right side nav links
    let rightVisibleCount = visibleRightNavLinks.length;

    if (rightVisibleCount === 0) {
      setIsWholeNavbarParsed(true);
      return;
    }
    for (let i = 0; i < visibleRightNavLinks.length; i++) {
      const el = rightNavLinkRefs.current[i];
      if (i === rightVisibleCount - 1) {
        setIsWholeNavbarParsed(true);
      }
      if (!el) continue;

      if (isTextMultiline(el)) {
        rightVisibleCount = Math.max(0, rightVisibleCount - i - 1);
        setVisibleRightNavLinks(initialRightNavLinks.slice(0, rightVisibleCount));
        setRightDropdownNavLinks(initialRightNavLinks.slice(rightVisibleCount));
        return;
      }
    }
  };

  useEffect(() => {
    if (isDesktop) {
      setVisibleLeftNavLinks(initialLeftNavLinks);
      setVisibleRightNavLinks(initialRightNavLinks);
      setLeftDropdownNavLinks([]);
      setRightDropdownNavLinks([]);
      setIsWholeNavbarParsed(false);
    }
  }, [
    navLinks,
    isDesktop,
    isLogoLoaded,
    rightButton.toggle,
    globalStyles.bodyText,
    localStyles?.navbarLinkStyles?.fontSize,
    localStyles?.navbarLinkStyles?.fontWeight,
    localStyles?.navbarLinkStyles?.letterSpacing,
    allCapsToggle,
  ]);

  useEffect(() => {
    const scrollContainer = document.querySelector(`#${BUILDER_EDITOR_ELEMENT_ID}`) as HTMLElement | null;

    const handleScroll = (scrollTop: number) => {
      setScrolled(scrollTop > 0);
    };

    const handleResize = () => {
      setVisibleLeftNavLinks([...initialLeftNavLinks]);
      setVisibleRightNavLinks([...initialRightNavLinks]);
      setLeftDropdownNavLinks([]);
      setRightDropdownNavLinks([]);
      setIsWholeNavbarParsed(false);
    };

    // Add resize listener for both environments
    window.addEventListener('resize', handleResize);

    // Create a unified scroll handler that works in both environments
    const createScrollHandler = (getScrollTop: () => number, method: string) => {
      return () => {
        const currentScrollY = getScrollTop();
        handleScroll(currentScrollY);
      };
    };

    let scrollHandler: (() => void) | null = null;
    let cleanup: (() => void) | null = null;

    if (scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
      // Builder environment - use the container scroll if it's scrollable
      scrollHandler = createScrollHandler(() => scrollContainer.scrollTop, 'container');
      scrollContainer.addEventListener('scroll', scrollHandler, { passive: true });

      cleanup = () => {
        if (scrollHandler) {
          scrollContainer.removeEventListener('scroll', scrollHandler);
        }
        window.removeEventListener('resize', handleResize);
      };
    } else {
      // Deployed environment or non-scrollable container - use window scroll
      scrollHandler = createScrollHandler(() => window.scrollY || window.pageYOffset || 0, 'window');
      window.addEventListener('scroll', scrollHandler, { passive: true });

      cleanup = () => {
        if (scrollHandler) {
          window.removeEventListener('scroll', scrollHandler);
        }
        window.removeEventListener('resize', handleResize);
      };
    }

    // Return cleanup functions
    return cleanup;
  }, [headerState, initialLeftNavLinks, initialRightNavLinks]);

  useEffect(() => {
    if (isDesktop) {
      let interval: any = null;
      setTimeout(
        () => {
          interval = setInterval(() => {
            if (
              !initialLeftNavLinks.length ||
              !initialRightNavLinks.length ||
              leftNavLinkRefs.current.length > 0 ||
              rightNavLinkRefs.current.length > 0
            ) {
              checkNavLinksWrapping();
              clearInterval(interval);
            }
          }, 100);
        },
        builderMode ? 0 : 250
      );
      return () => clearInterval(interval);
    }
  }, [
    visibleLeftNavLinks,
    visibleRightNavLinks,
    rightButton.toggle,
    isLogoLoaded,
    isDesktop,
    globalStyles.bodyText,
    localStyles?.navbarLinkStyles?.fontSize,
    localStyles?.navbarLinkStyles?.fontWeight,
    localStyles?.navbarLinkStyles?.letterSpacing,
    allCapsToggle,
  ]);

  const headerHeight = isDesktop ? '110px' : '80px';

  const renderChildDropDown = (hasChildren: boolean, index: string, navLink: ILink, isLeft: boolean) => {
    const isHoverd = hoveredLinkIndex === navLink.id;
    return (
      <>
        {hasChildren && isDesktop && isHoverd ? (
          <BoxComponent
            onMouseEnter={() => hasChildren && isDesktop && handleLinkHover(navLink.id)}
            onMouseLeave={() => hasChildren && isDesktop && handleLinkHover(null)}
            sx={{
              ...dropdownMenuStyles(scrolled),
              left: 0,
              right: 'auto',
            }}
          >
            {navLink.children?.map((childLink: ILink, childIndex: number) =>
              renderNavLink(
                childLink,
                `${index}-${childIndex}`,
                isLeft,
                true,
                true,
                false,
                navLink.children.length - 1 === childIndex
              )
            )}
          </BoxComponent>
        ) : (
          <></>
        )}
      </>
    );
  };

  const renderNavLink = (
    navLink: ILink,
    index: string,
    isLeft: boolean,
    isDropdown = false,
    isChild?: boolean,
    isSubChild?: boolean,
    isLast?: boolean
  ) => {
    const hasChildren = navLink.children && navLink.children.length > 0;
    const linkStyles = isDropdown
      ? {
          ...dropdownItemStyles(isChild, hasChildren, isSubChild, isLast),
          ...getBuilderDesktopStyles(navLink, builderConfig, navLinkStyles, localStyles),
        }
      : getBuilderDesktopStyles(navLink, builderConfig, navLinkStyles, localStyles);

    const refCollection: any = isLeft ? leftNavLinkRefs : rightNavLinkRefs;

    if (!previewMode && !builderMode) {
      return builderConfig ? (
        <Box
          onMouseEnter={() => hasChildren && isDesktop && !isDropdown && handleLinkHover(navLink.id)}
          onMouseLeave={() => hasChildren && isDesktop && !isDropdown && handleLinkHover(null)}
          sx={navlinkWrapperStyles(isDropdown, !!isDesktop)}
        >
          <StyledLink
            key={index}
            LinkComponent={LinkComponent}
            ref={(el: any) => !isChild && !isDropdown && (refCollection.current[index] = el)}
            style={
              isDropdown
                ? {
                    ...dropdownItemStyles(isChild, hasChildren, isSubChild, isLast),
                    ...getDesktopStyles(navLink, builderConfig, navLinkStyles, localStyles, pageUrls),
                  }
                : getDesktopStyles(navLink, builderConfig, navLinkStyles, localStyles, pageUrls)
            }
            target={navLink.internal ? '_self' : '_blank'}
            to={`${navLink.internal ? '/' : ''}${generateUrl(navLink, pageUrls)}`}
          >
            {truncateLabel(navLink.label)}
            {hasChildren && isDesktop && !isDropdown && <ExpandMoreIcon sx={{ marginLeft: '4px' }} />}
          </StyledLink>
          {!isDropdown && (renderChildDropDown(hasChildren, index, navLink, isLeft) as any)}
        </Box>
      ) : null;
    }

    return builderConfig ? (
      <Box
        id={navLink.id}
        key={navLink.id}
        onMouseEnter={() => hasChildren && isDesktop && !isDropdown && handleLinkHover(navLink.id)}
        onMouseLeave={() => hasChildren && isDesktop && !isDropdown && handleLinkHover(null)}
        sx={navlinkWrapperStyles(isDropdown, !!isDesktop)}
      >
        <BaseLink
          ref={(el) => !isChild && !isDropdown && (refCollection.current[index] = el)}
          href={builderMode ? blockLinks : `${navLink.internal ? '/' : ''}${navLink.link}`}
          target={builderMode ? '_self' : navLink.internal ? '_self' : '_blank'}
          sx={linkStyles}
          onClick={(e) => {
            if (builderMode && !previewMode && onTextSelect) {
              e.preventDefault();
              e.stopPropagation();
              onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey: 'navlink' });
            } else {
              handleNavLinkClick(navLink);
            }
          }}
        >
          {truncateLabel(navLink.label)}
          {hasChildren && isDesktop && !isDropdown && <ExpandMoreIcon sx={{ marginLeft: '4px' }} />}
        </BaseLink>
        {!isDropdown && renderChildDropDown(hasChildren, index, navLink, isLeft)}
      </Box>
    ) : null;
  };

  return (
    <>
      <BoxComponent component="header" sx={getHeaderStyles()}>
        <BoxComponent
          ref={headerRef}
          sx={
            isDesktop
              ? {
                  ...styles.HeaderContainer,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '20px 100px',
                }
              : { ...styles.MobileHeaderContainer, padding: isActive ? '0px 35px' : '0px 25px' }
          }
        >
          {isDesktop && visibleLeftNavLinks.length > 0 && (
            <BoxComponent
              ref={leftNavLinksContainerRef}
              sx={
                isDesktop
                  ? { ...updatedStyles.NavSection, ...updatedStyles.LeftNavSection }
                  : updatedStyles.LeftNavSection_Mobile
              }
            >
              <BoxComponent
                component="nav"
                aria-label="Primary navigation left"
                sx={navLinkContainerStyles(isActive, 'left', isDesktop)}
              >
                {!isActive &&
                  visibleLeftNavLinks.map((navLink, index) => renderNavLink(navLink, index.toString(), true))}
              </BoxComponent>
            </BoxComponent>
          )}

          <BoxComponent
            ref={logoContainerRef}
            sx={{
              ...styles.ImageContainer,
              ...updatedStyles.LogoContainer,
              display: !isDesktop && isActive ? 'none' : 'flex',
            }}
          >
            {!previewMode && !builderMode ? (
              <UniversalLink
                LinkComponent={LinkComponent}
                ref={logoContainerRef}
                to={logoProperties.link || '/'}
                style={isPublishedDesktopMode ? {} : { ...styles.Logo_Mobile }}
              >
                <Image
                  imageUrl={getLogoImage()}
                  altText={logoProperties.altText || logoProperties.name || 'logo image'}
                  placeholderImageStyle={{ minWidth: '170px' }}
                  MediaMap={MediaMap}
                  height={isDesktop ? '80px' : '50px'}
                  style={{ maxWidth: isDesktop ? '400px' : '100%', maxHeight: isDesktop ? '80px' : '50px' }}
                  builderMode={builderMode}
                  onLoad={() => {
                    setIsLogoLoaded(true);
                  }}
                />
              </UniversalLink>
            ) : (
              <BaseLink href={!previewMode && builderMode ? blockLinks : logoProperties.link || '/'} sx={styles.Logo}>
                <img
                  height={isDesktop ? '80px' : '50px'}
                  style={{ maxWidth: isDesktop ? '400px' : '100%', maxHeight: isDesktop ? '80px' : '50px' }}
                  onLoad={() => {
                    setIsLogoLoaded(true);
                  }}
                  src={getLogoImage()}
                  alt={logoProperties.altText || logoProperties.name || 'logo image'}
                />
              </BaseLink>
            )}
          </BoxComponent>

          <BoxComponent
            ref={rightNavLinksContainerRef}
            sx={{ ...updatedStyles.NavSection, ...updatedStyles.RightNavSection }}
          >
            {isDesktop ? (
              <BoxComponent
                component="nav"
                aria-label="Primary navigation right"
                sx={navLinkContainerStyles(isActive, 'right', isDesktop)}
              >
                {showNavLinks &&
                  isDesktop &&
                  visibleRightNavLinks.map((navLink, index) => renderNavLink(navLink, index.toString(), false))}

                {(leftDropdownNavLinks.length || rightDropdownNavLinks.length) > 0 && (
                  <BoxComponent
                    sx={dropdownContainerStyles}
                    onMouseEnter={() => handleMoreButtonHover(true)}
                    onMouseLeave={() => handleMoreButtonHover(false)}
                  >
                    <BoxComponent sx={dropdownButtonStyles}>
                      More <ExpandMoreIcon sx={{ marginLeft: '4px' }} />
                    </BoxComponent>
                    <BoxComponent
                      sx={{ ...dropdownMenuStyles(scrolled), display: hoveredMoreButton ? 'block' : 'none' }}
                    >
                      {leftDropdownNavLinks.map((navLink, index) => (
                        <React.Fragment key={navLink.id || index}>
                          {renderNavLink(navLink, index.toString(), false, true, true, false, false)}
                          {navLink.children?.map((childLink, childIndex) =>
                            renderNavLink(
                              childLink,
                              `dropdown-child-${index}-${childIndex}`,
                              false,
                              true,
                              false,
                              true,
                              navLink.children.length - 1 === childIndex
                            )
                          )}
                        </React.Fragment>
                      ))}
                      {rightDropdownNavLinks.map((navLink, index) => (
                        <React.Fragment key={navLink.id || index}>
                          {renderNavLink(
                            navLink,
                            index.toString(),
                            false,
                            true,
                            true,
                            false,
                            rightDropdownNavLinks.length - 1 === index
                          )}
                          {navLink.children?.map((childLink, childIndex) =>
                            renderNavLink(
                              childLink,
                              `dropdown-child-${index}-${childIndex}`,
                              false,
                              true,
                              false,
                              true,
                              navLink.children.length - 1 === childIndex
                            )
                          )}
                        </React.Fragment>
                      ))}
                    </BoxComponent>
                  </BoxComponent>
                )}

                {rightButton.toggle && (
                  <BoxComponent
                    ref={buttonContainerRef}
                    sx={isDesktop ? styles.NavButtonContainer : styles.NavButtonContainer_Mobile}
                  >
                    <CtaButton
                      builderMode={builderMode}
                      previewMode={previewMode}
                      buttonConfig={rightButton}
                      buttonStyles={buttonStyles}
                      pageUrls={pageUrls}
                      LinkComponent={LinkComponent}
                      onButtonClick={
                        onTextSelect
                          ? (dataKey: string) => {
                              onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey });
                            }
                          : undefined
                      }
                      dataKey="button.text"
                    />
                  </BoxComponent>
                )}
              </BoxComponent>
            ) : (
              <React.Suspense fallback={null}>
                <MobileVariant
                  navLinks={navLinks}
                  builderMode={builderMode}
                  previewMode={previewMode}
                  rightButton={rightButton}
                  buttonStyles={buttonStyles}
                  pageUrls={pageUrls}
                  navLinkStyles_Mobile={navLinkStyles_Mobile}
                  dropdownMobileItemStyles={dropdownMobileItemStyles}
                  builderConfig={builderConfig}
                  navlinkWrapperStyles={navlinkWrapperStyles}
                  navLinkContainerStyles={navLinkContainerStyles}
                  localStyles={localStyles}
                  isActive={isActive}
                  setIsActive={setIsActive}
                />
              </React.Suspense>
            )}
          </BoxComponent>

          {!isDesktop && ((showNavLinks && Boolean(navLinks.length)) || rightButton.toggle) && (
            <BoxComponent
              sx={{
                ...styles.MenuButtonContainer_Mobile,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: isActive ? '100%' : 'auto',
                justifyContent: 'space-between',
                marginTop: isActive ? '20px' : '0px',
              }}
            >
              {isActive && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    src={
                      headerState === HEADER_STATE.SCROLL_UP && scrolled
                        ? logoSecondaryProperties.image
                        : logoProperties.image
                    }
                    alt={logoProperties.altText}
                    style={{ height: '50px', maxWidth: '100px' }}
                  />
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {rightButton.toggle && !builderConfig?.stickyButtonToggle && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CtaButton
                      builderMode={builderMode}
                      previewMode={previewMode}
                      buttonConfig={rightButton}
                      buttonStyles={buttonStyles}
                      pageUrls={pageUrls}
                      onButtonClick={
                        onTextSelect
                          ? (dataKey: string) => {
                              onTextSelect({ text: '', range: { start: 0, end: 0 }, dataKey });
                            }
                          : undefined
                      }
                      dataKey="button.text"
                    />
                  </Box>
                )}
                <BoxComponent
                  sx={{ cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleActiveClass();
                  }}
                >
                  {isActive ? (
                    <CloseIcon sx={{ ...IconStyles, width: '40px', height: '40px' }} />
                  ) : (
                    <MenuIcon sx={{ ...IconStyles, width: '40px', height: '40px' }} />
                  )}
                </BoxComponent>
              </Box>
            </BoxComponent>
          )}
        </BoxComponent>
      </BoxComponent>
      {headerState === HEADER_STATE.STICKY && <BoxComponent sx={{ height: headerHeight }} />}
    </>
  );
};

export default HeaderVariantTwo;
